<template>
  <div class="relative flex items-center space-x-2">
    <button
      v-if="column.filterComponent?.component"
      type="button"
      class="h-6 w-6 cursor-pointer rounded-sm"
      :class="filterIconClasses"
      :data-cy="`table-header-filter-${column.key}-filter`"
    >
      <icon-base
        :icon="IconFilter"
        height="12"
        width="12"
        viewBox="0 0 16 16"
      />
      <div
        v-show="toggleFilter"
        class="absolute z-50"
        :class="{ 'pointer-events-none': !toggleFilter }"
      >
        <component
          :is="column.filterComponent.component"
          v-if="
            initialComponentRender || (!initialComponentRender && toggleFilter)
          "
          v-on-click-outside="onClickOutsideHandler"
          v-bind="mergedProps"
          :toggle-from-parent="toggleFilter"
          :is-loading="isLoading"
          :model-value:is-exclude-mode-enabled="state?.isExcludeModeEnabled"
          @update:model-value="column.filterComponent.onValueChange"
          @change="column.filterComponent.onChange"
          @loaded-more="column.filterComponent.loadedMore"
          @options-search="column.filterComponent.optionsSearch"
          @toggle-visible="handleFilterVisibilityChange"
          @update:is-exclude-mode-enabled="
            column.filterComponent.excludeModeChanged
          "
        ></component>
      </div>
    </button>
    <span class="flex items-center space-x-1">
      <span
        v-if="state?.numberOfFilters && state?.plural"
        class="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-center text-xxs leading-5 text-white"
      >
        <span>
          {{ state.numberOfFilters }}
        </span>
      </span>
      <span>
        {{ columnLabel }}
      </span>
    </span>
    <div
      v-if="hasFiltersSet && column.filterComponent?.onClear"
      class="translate-y-0-25 transform"
      :data-cy="`clear-filter-${column.filterComponent.staticProps?.name}`"
      @click.stop="column.filterComponent.onClear"
    >
      <icon-base icon="x-circle" />
    </div>

    <span
      v-if="typeof column.filterComponent?.sortByChange === 'function'"
      class="translate-y-0-25 transform"
      :data-cy="`table-header-filter-${column.key}-sort`"
      @click.prevent.stop="
        column.filterComponent.sortByChange(
          column.filterComponent.staticProps?.name as string
        )
      "
    >
      <div>
        <icon-base
          v-if="sortByActive"
          :data-cy="`sort-by-${column.filterComponent.staticProps?.name}`"
          icon="sort-arrow-active"
          height="18"
          width="18"
          :class="{
            'rotate-180 transform': rotateSortByArrow,
          }"
        />
        <icon-base
          v-else
          :data-cy="`sort-by-${column.filterComponent.staticProps?.name}`"
          icon="sort-arrow"
          height="18"
          width="18"
        />
      </div>
    </span>
  </div>
</template>
<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";
import { useStore } from "vuex";

import { FilterStateMapKey } from "@/helpers/symbols";

import IconFilter from "@/components/icons/IconFilter.vue";

import type { TableColumnV2 } from "@/models/applications";

const {
  column,
  toggleFilter = false,
  initialComponentRender = true,
} = defineProps<{
  column: TableColumnV2;
  toggleFilter?: boolean;
  initialComponentRender?: boolean;
}>();

const emit = defineEmits<{
  closeFilter: [];
}>();

const { dispatch } = useStore();
const isLoading = ref(false);

const filterStateMap = inject(FilterStateMapKey, {});
const state = computed(() => {
  const byKey = filterStateMap[column.key];

  if (byKey) {
    return byKey;
  }

  // Fall back to filter name for duplicate-key columns
  const filterName = column.filterComponent?.staticProps?.name as string;
  return filterName ? filterStateMap[filterName] : undefined;
});

const mergedProps = computed(() => {
  const staticProps = column.filterComponent?.staticProps || {};
  const filterState = state.value;

  if (!filterState) {
    return staticProps;
  }

  const merged: Record<string, unknown> = { ...staticProps };
  merged.modelValue = filterState.modelValue;

  // For options: prefer filterStateMap options, fallback to staticProps options
  if (filterState.options !== undefined) {
    // Date-picker columns store { formatter, disabled } in options
    if (
      typeof filterState.options === "object" &&
      filterState.options !== null &&
      "formatter" in (filterState.options as Record<string, unknown>)
    ) {
      const dateOpts = filterState.options as Record<string, unknown>;
      merged.formatter = dateOpts.formatter;
      merged.disabled = dateOpts.disabled;
    } else if (
      typeof filterState.options === "object" &&
      filterState.options !== null &&
      "documentsCompleted" in (filterState.options as Record<string, unknown>)
    ) {
      // DocumentsFilter stores { documentsCompleted } in options
      const docOpts = filterState.options as Record<string, unknown>;
      merged.documentsCompleted = docOpts.documentsCompleted;
    } else {
      merged.options = filterState.options;
    }
  }

  // CallsFilter columns store reactive props in modelValue
  if (
    typeof filterState.modelValue === "object" &&
    filterState.modelValue !== null &&
    "numberOfCallsRange" in (filterState.modelValue as Record<string, unknown>)
  ) {
    const callProps = filterState.modelValue as Record<string, unknown>;
    merged.numberOfCallsRange = callProps.numberOfCallsRange;
    merged.dateRange = callProps.dateRange;
    delete merged.modelValue;
  }

  if (filterState.isExcludeModeEnabled !== undefined) {
    merged.isExcludeModeEnabled = filterState.isExcludeModeEnabled;
  }

  return merged;
});

const filterIconClasses = computed(() => {
  if (toggleFilter) {
    return "bg-primary/10 text-primary";
  }
  const className = hasFiltersSet.value ? "text-primary" : "text-label";
  return `${className} hover:bg-grey-hover`;
});

const hasFiltersSet = computed(() => {
  return state.value?.hasFiltersSet || (state.value?.numberOfFilters ?? 0) > 0;
});

const columnLabel = computed(() => {
  const numFilters = state.value?.numberOfFilters ?? 0;
  return numFilters > 1 || (!numFilters && hasFiltersSet.value)
    ? state.value?.plural
    : column?.label;
});

const sortByActive = computed(() => {
  const name = column.filterComponent?.staticProps?.name as string;
  return !!state.value?.sortBy?.includes(name);
});

const rotateSortByArrow = computed(() => !state.value?.sortBy?.includes("-"));

const onClickOutsideHandler = [
  () => {
    if (initialComponentRender && !toggleFilter) {
      return;
    }
    emit("closeFilter");
  },
  { capture: initialComponentRender },
];

const handleFilterVisibilityChange = (value: boolean) => {
  if (!value) {
    emit("closeFilter");
  }
};

watch(
  () => toggleFilter,
  async (newToggleVal) => {
    if (column?.filterComponent?.lazyAction?.action && newToggleVal) {
      isLoading.value = true;
      try {
        await dispatch(
          column.filterComponent.lazyAction.action,
          column.filterComponent.lazyAction.payload
        );
      } finally {
        isLoading.value = false;
      }
    }
  }
);
</script>
